'use client'

import { Row, Stack } from '@christiankaindl/lyts'
import { ComboboxItem, ComboboxPopover } from '@ariakit/react'
import { memo } from 'react'
import SearchProvider, { useSearchResults } from './SearchProvider'
import { BookMarked as LawIcon } from 'lucide-react'
import * as styles from './Search.css'
import { ResultItem } from './Search'
import { lawHitUrl } from '@shared/lib/utils'
import Button from '@shared/components/Button/Button'
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip/Tooltip'
import { tooltipInfo } from '@shared/components/Law/law.css'
import { theme } from '@shared/styles/theme.css'
import Text from '../Text/Text'
import type { IDocument } from '@shared/lib/search.server'

const SearchPopover = memo(function SearchPopover({
  query,
  slackMsgEnvironmentPrefix,
}: {
  query: string
  slackMsgEnvironmentPrefix: string
}) {
  return (
    <ComboboxPopover
      unmountOnHide
      flip={false}
      sameWidth
      gutter={8}
      className={styles.suggestionsContainer}
    >
      <SearchPopoverContent
        query={query}
        slackMsgEnvironmentPrefix={slackMsgEnvironmentPrefix}
      />
    </ComboboxPopover>
  )
})
export default SearchPopover

export const SearchPopoverContent = memo(function SearchPopoverContent({
  query,
  slackMsgEnvironmentPrefix,
}: {
  query: string
  slackMsgEnvironmentPrefix: string
}) {
  return (
    <>
      <SearchProvider
        slackMsgEnvironmentPrefix={slackMsgEnvironmentPrefix}
        sortBy={['_text_match']}
        id='rich-results'
        query={query}
        minCharacters={0}
        params={{
          q: query,
          per_page: 7,
          query_by_weights: '150,1',
          query_by: 'citation,title',
          filter_by: 'documentType:law || documentType:clause',
          text_match_type: 'max_weight',
          stopwords: undefined,
          include_fields:
            'id,normId,abbreviation,lawTitle,lawType,documentType,enumeration,title,jurisdiction,state,sourceUrl',
          unset_params: ['group_by', 'facet_by'],
        }}
      >
        {query && <RichResultIndex />}
      </SearchProvider>

      <SearchProvider
        slackMsgEnvironmentPrefix={slackMsgEnvironmentPrefix}
        minCharacters={0}
        sortBy={['_text_match', 'count']}
        id='query-suggestions'
        query={query || '*'}
        indexName='query_suggestions'
        params={{
          q: query || '*',
          per_page: 7,
          query_by: 'q',
          stopwords: undefined,
          unset_params: ['group_by', 'facet_by', 'filter_by'],
        }}
      >
        <QuerySuggestions query={query} />
      </SearchProvider>
    </>
  )
})

function QuerySuggestions({ query }: { query: string }) {
  const results = useSearchResults()

  return (
    <>
      {results?.hits?.map((hit) => {
        if (hit === undefined) return null
        return (
          <ComboboxItem
            hideOnClick
            render={<ResultItem hit={hit.document} />}
            key={hit.document.id}
            value={hit.document.q}
          />
        )
      })}
      {results?.hits?.length === 0 && query && (
        <ComboboxItem
          hideOnClick
          render={
            <ResultItem hit={{ q: `Suche nach ${query}` }} query={query} />
          }
          value={query}
        />
      )}
    </>
  )
}

function RichResultIndex() {
  const results = useSearchResults()

  if (!results?.hits?.[0]?.document.abbreviation) {
    return null
  }

  return (
    <Row
      gap={0.5}
      style={{
        overflow: 'auto',
        scrollbarWidth: 'none',
        marginLeft: -6,
        marginRight: -6,
        padding: '6px 12px 3px',
      }}
    >
      {results?.hits.map((hit) => {
        return <RichResult key={hit.document.id} hit={hit.document} />
      })}
    </Row>
  )
}

type RichResultProps = React.HTMLProps<HTMLDivElement> & {
  hit: IDocument
  onSelect?: () => void
  index?: number
}
export const RichResult = function RichResult({ hit }: RichResultProps) {
  const href = lawHitUrl(
    hit,
    hit.documentType === 'clause' ? hit.enumeration : undefined,
  )
  if (!href) return null

  return (
    <Tooltip placement='bottom-start'>
      <TooltipTrigger>
        <Button
          style={{
            color: '#A48757',
            backgroundColor: 'rgba(228, 177, 95, 0.2)',
          }}
          size='tiny'
          circular
          href={href}
        >
          {hit.documentType === 'law' && <LawIcon width={16} />}
          {hit.documentType === 'clause' && hit.enumeration && (
            <>{hit.enumeration}&ensp;</>
          )}
          {hit.abbreviation}
        </Button>
      </TooltipTrigger>
      <TooltipContent style={{ textAlign: 'left' }} gutter={3}>
        <Stack gap={0.25}>
          {hit.documentType === 'clause' && (
            <Text weight={600} size='small'>
              {hit.enumeration}&ensp;{hit.title}
            </Text>
          )}
          <Row
            tabIndex={0}
            gap={0.25}
            className={tooltipInfo}
            style={{ fontWeight: 600, color: theme.colors.text.brand }}
          >
            <LawIcon width={16} />
            <Text color='inherit' size='small'>
              {hit.lawTitle}
            </Text>
          </Row>
        </Stack>
      </TooltipContent>
    </Tooltip>
  )
}
